import { useAsync } from "react-use";
import useSWR from "swr";
import { browse, createTask, ensureLecture, getChannels, getSubjects, playlistUrl, useFetch, PLATFORM_VK, PLATFORM_YOUTUBE, Platform } from "../../shared/api";
import type { BrowseResult } from "../../shared/api";
import { ErrorDisplay, usePageTitle } from "../../shared/utils";
import { getLastDir, setLastDir } from "../../shared/lastDir";
import { useNavigate } from "react-router-dom";
import { useCallback, useState } from "react";
import { useSubmitForm } from "./hooks/useSubmitForm";
import { PlatformBlock } from "./components/PlatformBlock";
import { DescriptionSection } from "./components/DescriptionSection";
import { TemplatesPanel } from "./components/TemplatesPanel";
import type { TemplateData } from "./types";
import { FilePickerModal } from "../editor/components/FilePickerModal";
import { Panel } from "../../shared/components/Panel";
import { InlineSelect } from "../../shared/components/InlineSelect";
import { InlineRow } from "../../shared/components/InlineRow";
import { generateTitle, generateDescription, validate } from "./utils";
import { validateStructuredDescription } from "./utils";
import { ACCESS_PUBLIC, PLAYLIST_NONE } from "./utils";
import { Box, Title, Group, TextInput, Button, Text, Anchor } from "@mantine/core";

const SUBJECT_NONE = "none";

export function SubmitTask() {
  usePageTitle('Создание задачи');
  const navigate = useNavigate();
  const { fetch } = useFetch();
  const { value: channels } = useAsync(() => getChannels({ fetch }), [fetch]);
  const { data: subjects } = useSWR("admin/subjects", () => getSubjects({ fetch }));

  const {
    formState, setFormState, isTitleManualMode, setIsTitleManualMode,
    videoData, setVideoData, subjectValue, setSubjectValue,
    isManualMode, setIsManualMode, structuredDesc, setStructuredDesc,
  } = useSubmitForm();

  const updateForm = (p: Partial<typeof formState>) => setFormState(prev => ({ ...prev, ...p }));
  const subjectId = subjectValue && subjectValue !== SUBJECT_NONE ? Number(subjectValue) : null;
  const effectiveForm = { ...formState, title: isTitleManualMode ? formState.title : generateTitle(videoData), description: isManualMode ? formState.description : generateDescription(structuredDesc) };
  const descWarnings = validateStructuredDescription({ ...structuredDesc, title: effectiveForm.title, subjectName: videoData.subjectName, lectureName: videoData.lectureName });
  const validated = validate(effectiveForm);
  const submitHint = !validated.ok ? validated.error : subjectValue == null ? "Выберите предмет" : null;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [pickerTarget, setPickerTarget] = useState<'video' | 'thumbnail' | null>(null);
  const [browseResult, setBrowseResult] = useState<BrowseResult | null>(null);

  // /api/browse lists the output share.
  const browseOutput = useCallback(async (path: string, target: 'video' | 'thumbnail') => {
    const res = await browse({ path, mode: target === 'thumbnail' ? 'image' : 'video', fetch });
    setBrowseResult(res); setLastDir('output', res.path);
  }, [fetch]);

  const openPicker = useCallback(async (target: 'video' | 'thumbnail') => {
    setPickerTarget(target); setBrowseResult(null);
    await browseOutput(getLastDir('output'), target);
  }, [browseOutput]);

  const submit = useCallback(async () => {
    if (!validated.ok) return;
    setLoading(true); setError(null);
    try {
      let lectureId: number | undefined;
      if (!isTitleManualMode && subjectId != null) {
        const part = (name: string, id: string | null) => id ? { student_id: id } : name.trim() ? { student_name: name.trim() } : null;
        const res = await ensureLecture({ req: { subject_id: subjectId, number: videoData.lectureNumber, operator: part(structuredDesc.staff.operator, structuredDesc.staff.operatorId ?? null), editor: part(structuredDesc.staff.editor, structuredDesc.staff.editorId ?? null) }, fetch });
        lectureId = res.lecture_id;
      }
      const results = await Promise.allSettled(validated.tasks.map(task => createTask({ task: { ...task, lecture_id: lectureId }, fetch })));
      const ok = results.filter((r): r is PromiseFulfilledResult<any> => r.status === 'fulfilled').map(r => r.value);
      setLoading(false);
      if (ok.length > 0) navigate(`/tasks/${ok[0].id}`);
      else setError((results.find(r => r.status === 'rejected') as any)?.reason ?? new Error('Не удалось создать задачи'));
    } catch (err: any) { setError(err); setLoading(false); }
  }, [fetch, validated, navigate, isTitleManualMode, subjectId, videoData.lectureNumber, structuredDesc.staff]);

  const applyTemplate = (d: TemplateData, tSubjId?: number | null) => {
    if (tSubjId !== undefined) setSubjectValue(tSubjId != null ? String(tSubjId) : SUBJECT_NONE);
    if (d.subjectName != null) setVideoData(prev => ({ ...prev, subjectName: d.subjectName as string }));
    if (d.platforms) updateForm({ platforms: d.platforms });
    if (d.structured) setStructuredDesc({ ...structuredDesc, additionalMessage: d.structured.additionalMessage ?? structuredDesc.additionalMessage, metadata: { ...structuredDesc.metadata, lecturer: d.structured.metadata?.lecturer ?? structuredDesc.metadata.lecturer }, staff: { ...structuredDesc.staff, ...d.structured.staff }, references: { ...structuredDesc.references, ...d.structured.references } });
  };

  const subject = subjects?.find(s => s.id === subjectId) ?? null;
  const subjectPlaylist = (p: Platform) => subject?.playlists?.find(l => l.platform === p) ?? null;

  // Picking a subject is the one place that decides playlists: every platform is
  // enabled and gets the subject's playlist (or is left to choose), and the
  // description links point at the subject's playlists. Changing the subject
  // again always overwrites; afterwards everything can still be edited by hand,
  // with a warning on playlists that drift from the subject. Templates bypass
  // this and restore exactly what they saved.
  const changeSubject = (value: string | null) => {
    setSubjectValue(value);
    const subj = subjects?.find(s => String(s.id) === value);
    if (!subj) return;
    const links = subj.playlists ?? [];
    updateForm({
      platforms: [PLATFORM_YOUTUBE, PLATFORM_VK].map(p => {
        const prev = formState.platforms.find(x => x.platform === p);
        const link = links.find(l => l.platform === p);
        return { platform: p, access: prev?.access ?? ACCESS_PUBLIC, channel_ref: link?.channel_id ?? prev?.channel_ref ?? '', playlist_ref: link?.playlist_id ?? '' };
      }),
    });
    const ref = (p: Platform) => { const l = links.find(x => x.platform === p); return l ? playlistUrl(p, l.channel_id, l.playlist_id) : ''; };
    setStructuredDesc(prev => ({ ...prev, references: { youtube: ref(PLATFORM_YOUTUBE), vk: ref(PLATFORM_VK) } }));
  };

  // A playlist picked by hand also updates that platform's link in the description.
  const onPlatformPatch = (p: Platform, patch: Partial<typeof formState.platforms[0]>) => {
    patchPlatform(p, patch);
    if (patch.playlist_ref === undefined || patch.channel_ref !== undefined) return;
    const ch = patch.channel_ref ?? formState.platforms.find(x => x.platform === p)?.channel_ref ?? '';
    const url = patch.playlist_ref && patch.playlist_ref !== PLAYLIST_NONE && ch ? playlistUrl(p, ch, patch.playlist_ref) : '';
    setStructuredDesc(prev => ({ ...prev, references: { ...prev.references, [p]: url } }));
  };

  const isEnabled = (p: Platform) => formState.platforms.some(x => x.platform === p);
  const togglePlatform = (p: Platform, en: boolean) => updateForm({ platforms: en ? [...formState.platforms, { platform: p, channel_ref: '', playlist_ref: '', access: ACCESS_PUBLIC }] : formState.platforms.filter(x => x.platform !== p) });
  const patchPlatform = (p: Platform, patch: Partial<typeof formState.platforms[0]>) => updateForm({ platforms: isEnabled(p) ? formState.platforms.map(x => x.platform === p ? { ...x, ...patch } : x) : [...formState.platforms, { platform: p, channel_ref: '', playlist_ref: '', access: ACCESS_PUBLIC, ...patch }] });

  return (
    <Box maw={1500} mx="auto" px={40} py="xl">
      <Title order={3} mb="xl">Создание задачи</Title>
      <Group align="flex-start" gap={48} wrap="nowrap">
        <Box w={280} style={{ flexShrink: 0 }}>
          <TemplatesPanel snapshot={{ subjectName: videoData.subjectName, platforms: formState.platforms, structured: { additionalMessage: structuredDesc.additionalMessage, metadata: { lecturer: structuredDesc.metadata.lecturer }, staff: structuredDesc.staff, references: structuredDesc.references } }} currentSubjectId={subjectId} onApply={applyTemplate} />
        </Box>
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Panel title="Предмет">
            <InlineSelect label="Предмет" required searchable labelWidth={160} value={subjectValue} data={(subjects ?? []).map(s => ({ value: String(s.id), label: s.title }))} pinned={{ value: SUBJECT_NONE, label: "[Не указан]" }} placeholder="Выберите предмет" onChange={changeSubject} />
          </Panel>
          <Panel title="Платформы">
            <Group grow align="flex-start" gap="xl" wrap="nowrap">
              {[{ platform: PLATFORM_YOUTUBE, name: "YouTube" }, { platform: PLATFORM_VK, name: "VK" }].map(({ platform, name }) => (
                <PlatformBlock key={platform} platform={platform} platformName={name} isEnabled={isEnabled(platform)} channels={channels} formState={formState} matchingPlaylists={channels?.[platform]?.find((c: any) => c.id === formState.platforms.find(x => x.platform === platform)?.channel_ref)?.playlists || []} subjectPlaylist={subjectPlaylist(platform)} onToggle={en => togglePlatform(platform, en)} onPatch={patch => onPlatformPatch(platform, patch)} />
              ))}
            </Group>
          </Panel>
          <Panel title="Данные">
            <InlineRow label="Путь к видео" required labelWidth={160} value={formState.path || null} placeholder="Выберите видео" onClick={() => openPicker('video')} />
            <InlineRow label="Путь к обложке" required labelWidth={160} value={formState.thumbnail_path || null} placeholder="Выберите обложку" onClick={() => openPicker('thumbnail')} />
          </Panel>
          <Panel title="Видео" headerRight={<Anchor component="button" type="button" c="dimmed" fz="sm" td="underline" onClick={() => setIsTitleManualMode(!isTitleManualMode)}>{isTitleManualMode ? 'Заполнить вручную' : 'По формату'}</Anchor>}>
            {isTitleManualMode ? <TextInput label="Название видео" withAsterisk value={formState.title} onChange={e => updateForm({ title: e.currentTarget.value })} /> : (
              <Group align="flex-start" gap="md" wrap="wrap">
                <TextInput label="Название предмета" withAsterisk w={300} value={videoData.subjectName} onChange={e => setVideoData(prev => ({ ...prev, subjectName: e.target.value }))} />
                <TextInput label="№" w={70} inputMode="numeric" value={videoData.lectureNumber} onChange={e => setVideoData(prev => ({ ...prev, lectureNumber: parseInt(e.target.value) || 1 }))} />
                <TextInput label="Название лекции" w={500} value={videoData.lectureName} onChange={e => setVideoData(prev => ({ ...prev, lectureName: e.target.value }))} />
              </Group>
            )}
          </Panel>
          <Panel title="Описание" headerRight={<Anchor component="button" type="button" c="dimmed" fz="sm" td="underline" onClick={() => setIsManualMode(!isManualMode)}>{isManualMode ? 'Заполнить вручную' : 'По формату'}</Anchor>}>
            <DescriptionSection isManualMode={isManualMode} formState={formState} structuredDesc={structuredDesc} generatedDescription={generateDescription(structuredDesc)} warnings={descWarnings} onUpdateFormState={updateForm} onUpdateStructuredDesc={setStructuredDesc} />
          </Panel>
          {error && <ErrorDisplay error={error} />}
          <Group gap="md" mt="lg" align="center">
            <Button size="md" onClick={submit} loading={loading} disabled={!validated.ok || subjectValue == null}>Отправить</Button>
            {submitHint && <Text c="orange" fz="sm">{submitHint}</Text>}
          </Group>
        </Box>
      </Group>
      <FilePickerModal open={pickerTarget !== null} browseResult={browseResult} showDirConfirm={false} onClose={() => setPickerTarget(null)} onBrowse={path => pickerTarget && browseOutput(path, pickerTarget)} onSelect={path => { updateForm({ [pickerTarget === 'video' ? 'path' : 'thumbnail_path']: path }); setPickerTarget(null); }}
        imagePreviewUrl={pickerTarget === 'thumbnail' ? path => `/api/thumbs?${new URLSearchParams({ path })}` : undefined} />
    </Box>
  );
}
